/**
 * Pure form logic for the invoice-labeling flashcard (INP-651).
 *
 * Extracted from the route component so the prefill→form mapping, the
 * light convention normalization (INP-632), and the submit-payload build
 * are unit-testable without a browser. Field values are strings while
 * editing; the server (Pydantic) coerces ISO dates + numeric strings.
 */

import type { components } from '@/api/types';

export type LabelFields = components['schemas']['LabelFields'];
export type LabelSubmitIn = components['schemas']['LabelSubmitIn'];

export type DocumentType = NonNullable<LabelFields['document_type']>;
export type TaxIndication = NonNullable<LabelFields['tax_indication']>;
export type Jurisdiction = NonNullable<LabelFields['supplier_jurisdiction']>;

export const DOCUMENT_TYPES: DocumentType[] = [
  'invoice',
  'estimate',
  'statement_of_account',
  'engagement_letter',
  'unreadable',
  'other',
];

export const TAX_INDICATIONS: TaxIndication[] = [
  'amount_shown',
  'stated_included',
  'rate_shown',
  'none',
];

export const JURISDICTIONS: Jurisdiction[] = ['canada', 'us', 'other'];

/** All values as edit-time strings; enum fields are '' when unset.
 *  `absent_fields` holds the value-field keys the labeler marked "not on the
 *  document" — a confirmed negative, distinct from a left-blank field. */
export interface FormValues {
  document_type: '' | DocumentType;
  supplier_name_on_invoice: string;
  invoice_date: string;
  invoice_total: string;
  supplier_bn: string;
  tax_indication: '' | TaxIndication;
  tax_shown: string;
  invoice_number: string;
  recipient_name: string;
  payment_terms: string;
  supplier_jurisdiction: '' | Jurisdiction;
  notes: string;
  absent_fields: string[];
}

export function emptyForm(): FormValues {
  return {
    document_type: '',
    supplier_name_on_invoice: '',
    invoice_date: '',
    invoice_total: '',
    supplier_bn: '',
    tax_indication: '',
    tax_shown: '',
    invoice_number: '',
    recipient_name: '',
    payment_terms: '',
    supplier_jurisdiction: '',
    notes: '',
    absent_fields: [],
  };
}

/** Seed the form from the parser's prefill (null → blank field). */
export function formFromPrefill(prefill: LabelFields | null | undefined): FormValues {
  const base = emptyForm();
  if (!prefill) return base;
  return {
    ...base,
    document_type: prefill.document_type ?? '',
    supplier_name_on_invoice: prefill.supplier_name_on_invoice ?? '',
    invoice_date: prefill.invoice_date ?? '',
    invoice_total: prefill.invoice_total ?? '',
    supplier_bn: prefill.supplier_bn ?? '',
    tax_indication: prefill.tax_indication ?? '',
    tax_shown: prefill.tax_shown ?? '',
    invoice_number: prefill.invoice_number ?? '',
    recipient_name: prefill.recipient_name ?? '',
    payment_terms: prefill.payment_terms ?? '',
    supplier_jurisdiction: prefill.supplier_jurisdiction ?? '',
  };
}

/** "Net 30 days" / "net30" / "NET 30" → "Net 30". Leaves anything that
 *  isn't a plain net-N term untouched (trimmed). */
export function normalizePaymentTerms(value: string): string {
  const trimmed = value.trim();
  const match = /^net\s*(\d+)\s*(?:days?)?$/i.exec(trimmed);
  return match ? `Net ${match[1]}` : trimmed;
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** Build the label POST body: trim, blank→null, normalize payment terms,
 *  carry the prefill source so a correction is a traceable parser-error
 *  signal. Numeric/date strings pass through for the server to coerce. */
export function buildLabelPayload(
  values: FormValues,
  prefillSource: string | null,
): LabelSubmitIn {
  return {
    document_type: values.document_type || null,
    supplier_name_on_invoice: blankToNull(values.supplier_name_on_invoice),
    invoice_date: blankToNull(values.invoice_date),
    invoice_total: blankToNull(values.invoice_total),
    supplier_bn: blankToNull(values.supplier_bn),
    tax_indication: values.tax_indication || null,
    tax_shown: blankToNull(values.tax_shown),
    invoice_number: blankToNull(values.invoice_number),
    recipient_name: blankToNull(values.recipient_name),
    payment_terms: values.payment_terms.trim()
      ? normalizePaymentTerms(values.payment_terms)
      : null,
    supplier_jurisdiction: values.supplier_jurisdiction || null,
    notes: blankToNull(values.notes),
    prefill_source: prefillSource,
    absent_fields: values.absent_fields,
  };
}
